import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Send, FileDown, AlertTriangle, Banknote, Save, FileEdit, Trash2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import AmountInput from "@/components/AmountInput";

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}
function formatDateIT(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

const NoDestNotice = () => (
  <div className="mb-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="versamento-no-destinazione">
    L&apos;agente non ha abilitato nessuna modalità di versamento per la tua utenza. Contattalo.
  </div>
);

export default function Versamento() {
  const { user, refresh: reloadAuth } = useAuth();
  const isAgente = user?.role === "agente";
  const hasContoSeparato = user?.role === "collaboratore" && !!user?.conto_separato;
  const gestioneLiquiditaAttiva = !hasContoSeparato || user?.gestione_liquidita_attiva !== false;
  const allowAgenzia = user?.vers_agenzia_abilitato !== false;
  const allowCc = user?.vers_cc_abilitato !== false;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({}); // entry_id -> bool
  const [note, setNote] = useState("");
  const [destinazione, setDestinazione] = useState(isAgente ? "conto_corrente" : "agenzia");
  const ccSepFlow = hasContoSeparato && gestioneLiquiditaAttiva;
  const noDest = !isAgente && !ccSepFlow && !allowAgenzia && !allowCc;
  useEffect(() => {
    if (isAgente || ccSepFlow) return;
    if (!allowAgenzia && allowCc && destinazione !== "conto_corrente") setDestinazione("conto_corrente");
    if (!allowCc && allowAgenzia && destinazione !== "agenzia") setDestinazione("agenzia");
  }, [isAgente, ccSepFlow, allowAgenzia, allowCc, destinazione]);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [importoLiberoStr, setImportoLiberoStr] = useState("");

  const isImportoLiberoMode = data?.versamento_mode === "importo_libero";

  // Map UI destination to API destination query param
  const apiDestParam = useMemo(() => {
    if (isAgente) return "cc_separato";
    if (destinazione === "conto_corrente" || destinazione === "cc_separato") return "cc_separato";
    return "agenzia";
  }, [isAgente, destinazione]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [av, vs, dr] = await Promise.all([
        api.get("/versamenti/available", { params: { destinazione: apiDestParam } }),
        api.get("/versamenti"),
        api.get("/versamenti/drafts"),
      ]);
      setData(av.data);
      setHistory(vs.data);
      setDrafts(dr.data);
      setSelected({});
      setEditingDraftId(null);
      setNote("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [apiDestParam]);

  useEffect(() => { load(); }, [load]);

  const totalSelected = useMemo(() => {
    if (!data) return 0;
    return data.entries
      .filter((e) => selected[e.id])
      .reduce((s, e) => s + Number(e.importo || 0), 0);
  }, [selected, data]);

  // Liquidity breakdown by method (only for agent role; LIQUID_METHODS only):
  // shows Available / Selected (to deposit) / Pending (remaining in cash) per category.
  const liquidityBreakdown = useMemo(() => {
    if (!data?.entries) return null;
    const groups = {
      Contante: { label: "Contante", available: 0, selected: 0 },
      Assegno: { label: "Assegno", available: 0, selected: 0 },
      Recuperi: { label: "Recuperi", available: 0, selected: 0 },
      _other: { label: "Altri", available: 0, selected: 0 },
    };
    data.entries.forEach((e) => {
      const amt = Number(e.importo || 0);
      const key = e.metodo_pagamento === "Contante" ? "Contante"
        : e.metodo_pagamento === "Assegno" ? "Assegno"
        : (e.metodo_pagamento === "Recupero contante" || e.metodo_pagamento === "Recupero assegno") ? "Recuperi"
        : "_other";
      groups[key].available += amt;
      if (selected[e.id]) groups[key].selected += amt;
    });
    const rows = Object.entries(groups)
      .filter(([, v]) => v.available > 0 || v.selected > 0)
      .map(([k, v]) => ({ key: k, ...v, pending: v.available - v.selected }));
    const totalAvail = rows.reduce((s, r) => s + r.available, 0);
    const totalSel = rows.reduce((s, r) => s + r.selected, 0);
    return { rows, totalAvail, totalSel, totalPending: totalAvail - totalSel };
  }, [data, selected]);

  const countSelected = useMemo(() => Object.values(selected).filter(Boolean).length, [selected]);

  const toggleEntry = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleDay = (dayEntries, checked) => {
    setSelected((s) => {
      const next = { ...s };
      dayEntries.forEach((e) => {
        const inOther = e.in_draft_id && e.in_draft_id !== editingDraftId;
        if (!inOther) next[e.id] = checked;
      });
      return next;
    });
  };
  const allDaySelected = (dayEntries) => {
    const list = dayEntries.filter((e) => !(e.in_draft_id && e.in_draft_id !== editingDraftId));
    return list.length > 0 && list.every((e) => selected[e.id]);
  };

  const submit = async () => {
    const entry_ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (!entry_ids.length) {
      toast.error("Seleziona almeno una polizza");
      return;
    }
    if (!window.confirm(`Confermi il versamento di ${entry_ids.length} polizze per ${formatEUR(totalSelected)}?`)) return;
    setSubmitting(true);
    try {
      if (editingDraftId) {
        // First persist any change to the draft (in case user changed selection)
        await api.put(`/versamenti/drafts/${editingDraftId}`, { entry_ids, note, destinazione });
        await api.post(`/versamenti/drafts/${editingDraftId}/submit`);
      } else {
        await api.post("/versamenti", { entry_ids, note, destinazione });
      }
      toast.success("Versamento creato e inviato via email");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const submitImportoLibero = async () => {
    const amount = parseFloat(importoLiberoStr.replace(",", ".")) || 0;
    if (amount <= 0) {
      toast.error("Inserisci un importo valido maggiore di zero");
      return;
    }
    if (!window.confirm(`Confermi il versamento manuale di ${formatEUR(amount)}?`)) return;
    setSubmitting(true);
    try {
      await api.post("/versamenti", {
        entry_ids: [],
        importo_libero: amount,
        note,
        destinazione,
      });
      toast.success("Versamento manuale creato e inviato via email");
      setImportoLiberoStr("");
      setNote("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const saveDraft = async () => {
    const entry_ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (!entry_ids.length) {
      toast.error("Seleziona almeno una polizza per salvare la bozza");
      return;
    }
    setSubmitting(true);
    try {
      if (editingDraftId) {
        await api.put(`/versamenti/drafts/${editingDraftId}`, { entry_ids, note, destinazione });
        toast.success("Bozza aggiornata");
      } else {
        await api.post("/versamenti/drafts", { entry_ids, note, destinazione });
        toast.success("Bozza salvata. Potrai modificarla e inviarla in seguito.");
      }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const openDraft = (d) => {
    const next = {};
    (d.entry_ids || []).forEach((id) => { next[id] = true; });
    setSelected(next);
    setNote(d.note || "");
    setDestinazione(d.destinazione || (isAgente ? "conto_corrente" : "agenzia"));
    setEditingDraftId(d.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelDraftEdit = () => {
    setSelected({});
    setNote("");
    setEditingDraftId(null);
  };

  const deleteDraft = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa bozza?")) return;
    try {
      await api.delete(`/versamenti/drafts/${id}`);
      toast.success("Bozza eliminata");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadVersamento = async (id, cognome, dataV, kind) => {
    try {
      const res = await api.get(`/versamenti/${id}/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `versamento_${cognome || ""}_${dataV}.${kind === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <div className="text-slate-500 text-sm">Caricamento…</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Operazione</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">Versa Premi</h1>
          <p className="text-sm text-slate-500 mt-1">
            Seleziona le polizze che oggi versi sul conto di agenzia. Verrà inviato un riepilogo via email.
            {(data?.pos_aziendale || data?.conto_agenzia || data?.postale_agenzia || data?.is_agent) && (
              <span className="block text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded mt-2 inline-block" data-testid="versamento-filter-hint">
                {data?.is_agent && "Visibili solo polizze incassate in Contante / Assegno / Recupero (Bancomat, Bollettino e Bonifico sono già sul C/C di agenzia). "}
                {data?.pos_aziendale && "POS aziendale → Bancomat esclusi. "}
                {data?.conto_agenzia && "C/C agenzia → Bonifici esclusi. "}
                {data?.postale_agenzia && "Postale agenzia → Bollettini postali esclusi."}
              </span>
            )}
          </p>
        </div>
        <Link to="/foglio-cassa">
          <Button variant="outline" data-testid="back-to-sheet">Torna al foglio cassa</Button>
        </Link>
      </div>

      {/* Toggle Gestione liquidità (solo collab con conto_separato) */}
      {hasContoSeparato && (
        <Card className="p-4 border-slate-200 bg-slate-50/50 flex items-start justify-between gap-3" data-testid="gestione-liquidita-card">
          <div className="flex-1">
            <div className="font-semibold text-slate-900 inline-flex items-center gap-2">
              <Banknote className="w-4 h-4 text-emerald-600" />
              Gestione liquidità CC separato
            </div>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              {gestioneLiquiditaAttiva
                ? "Attiva: i tuoi incassi liquidi passano prima sul tuo CC separato (auto-convalida) e poi vanno in agenzia."
                : "Disattivata: tutti i premi vanno direttamente in agenzia (un solo step). Il CC separato e la distinta sono nascosti."}
            </p>
          </div>
          <Switch
            checked={gestioneLiquiditaAttiva}
            data-testid="toggle-gestione-liquidita"
            onCheckedChange={async (v) => {
              try {
                await api.put("/me/gestione-liquidita", { attiva: v });
                toast.success(v ? "Gestione liquidità attivata" : "Gestione liquidità disattivata");
                if (reloadAuth) await reloadAuth();
                await load();
              } catch (err) { toast.error(formatApiError(err)); }
            }}
          />
        </Card>
      )}

      {/* Liquidity breakdown — visible only when there are eligible entries */}
      {!isImportoLiberoMode && liquidityBreakdown && liquidityBreakdown.rows.length > 0 && (
        <Card className="p-5 border-amber-200 bg-amber-50/30" data-testid="liquidity-breakdown">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-amber-800 font-semibold inline-flex items-center gap-1.5">
                <Banknote className="w-3.5 h-3.5" /> Riepilogo liquidità
              </div>
              <h2 className="font-heading text-lg font-semibold text-slate-900">Cosa stai versando rispetto a quello che hai in cassa</h2>
              <p className="text-xs text-slate-600 mt-1">
                Confronta la <strong>liquidità disponibile</strong> (polizze incassate non ancora versate) con quanto stai per versare. La differenza è ciò che resta fisicamente in cassa.
              </p>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Totale liquidità</div>
              <div className="font-heading text-2xl font-bold text-amber-900 font-mono-num" data-testid="liquidity-total-avail">
                {formatEUR(liquidityBreakdown.totalAvail)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
            {liquidityBreakdown.rows.map((r) => (
              <div key={r.key} className="bg-white border border-amber-200 rounded-lg p-3" data-testid={`liquidity-row-${r.key}`}>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{r.label}</div>
                <div className="mt-1 space-y-0.5 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Disponibile</span><span className="font-mono-num font-medium">{formatEUR(r.available)}</span></div>
                  <div className="flex justify-between"><span className="text-blue-700">Selezionato</span><span className="font-mono-num font-semibold text-blue-700">{formatEUR(r.selected)}</span></div>
                  <div className="flex justify-between pt-0.5 border-t border-slate-100"><span className={r.pending > 0 ? "text-amber-700" : "text-slate-400"}>Resta in cassa</span><span className={`font-mono-num font-semibold ${r.pending > 0 ? "text-amber-700" : "text-slate-400"}`}>{formatEUR(r.pending)}</span></div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-3 border-t border-amber-200 grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Disponibile</div>
              <div className="font-mono-num font-bold text-slate-900">{formatEUR(liquidityBreakdown.totalAvail)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Selezionato</div>
              <div className="font-mono-num font-bold text-blue-700" data-testid="liquidity-total-selected">{formatEUR(liquidityBreakdown.totalSel)}</div>
            </div>
            <div>
              <div className={`text-[10px] uppercase tracking-wider font-semibold ${liquidityBreakdown.totalPending > 0 ? "text-amber-700" : "text-slate-400"}`}>Resta in cassa</div>
              <div className={`font-mono-num font-bold ${liquidityBreakdown.totalPending > 0 ? "text-amber-700" : "text-slate-400"}`} data-testid="liquidity-total-pending">{formatEUR(liquidityBreakdown.totalPending)}</div>
            </div>
          </div>
        </Card>
      )}

      {/* === MODALITÀ IMPORTO LIBERO === */}
      {isImportoLiberoMode && (
        <Card className="p-6 border-slate-200" data-testid="importo-libero-form">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Banknote className="w-4 h-4 text-emerald-700" />
            </div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">Versamento manuale</h2>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            La tua agenzia usa la modalità <strong>importo libero</strong>: inserisci l&apos;importo totale versato in banca senza spuntare le singole polizze.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Importo versato (€)</label>
              <AmountInput
                value={importoLiberoStr}
                onChange={(e) => setImportoLiberoStr(e.target.value)}
                placeholder="es. 1250,00"
                data-testid="importo-libero-input"
                className="mt-1 text-2xl font-bold text-slate-900"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Note (facoltative)</label>
              <Textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="es. Versamento bonifico online"
                data-testid="importo-libero-note"
                className="mt-1"
              />
            </div>
          </div>

          {/* Destinazione */}
          <div className="mt-4 pt-4 border-t border-slate-100">
            {isAgente ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 inline-flex items-start gap-2" data-testid="importo-libero-destinazione-fixed">
                <Banknote className="w-4 h-4 mt-0.5 text-slate-500" />
                <div>
                  <div className="font-semibold text-slate-900">Destinazione: C/C agenzia</div>
                </div>
              </div>
            ) : hasContoSeparato && gestioneLiquiditaAttiva ? (
              <>
                <div className="text-xs text-slate-500 uppercase font-semibold tracking-wider mb-2">Destinazione</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setDestinazione("conto_corrente")}
                    className={`text-left p-3 rounded-lg border ${destinazione === "conto_corrente" ? "border-emerald-700 bg-emerald-700 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
                    data-testid="il-dest-cc-separato"
                  >
                    <div className="font-semibold text-sm">🏦 CC separato</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDestinazione("agenzia")}
                    className={`text-left p-3 rounded-lg border ${destinazione === "agenzia" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
                    data-testid="il-dest-agenzia"
                  >
                    <div className="font-semibold text-sm">🏢 Agenzia</div>
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="text-xs text-slate-500 uppercase font-semibold tracking-wider mb-2">Destinazione</div>
                {!allowAgenzia && !allowCc && <NoDestNotice />}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {allowAgenzia && (
                  <button
                    type="button"
                    onClick={() => setDestinazione("agenzia")}
                    className={`text-left p-3 rounded-lg border ${destinazione === "agenzia" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
                    data-testid="il-dest-agenzia"
                  >
                    <div className="font-semibold text-sm">Versa in agenzia</div>
                  </button>
                  )}
                  {allowCc && (
                  <button
                    type="button"
                    onClick={() => setDestinazione("conto_corrente")}
                    className={`text-left p-3 rounded-lg border ${destinazione === "conto_corrente" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
                    data-testid="il-dest-cc"
                  >
                    <div className="font-semibold text-sm">Versa su C/C agenzia</div>
                  </button>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end mt-5">
            <Button
              onClick={submitImportoLibero}
              disabled={submitting || noDest || !importoLiberoStr || parseFloat(importoLiberoStr.replace(",", ".")) <= 0}
              size="lg"
              data-testid="importo-libero-submit"
            >
              <Send className="w-4 h-4 mr-2" />
              Conferma e invia
            </Button>
          </div>
        </Card>
      )}

      {/* Drafts panel */}
      {!isImportoLiberoMode && drafts.length > 0 && (
        <Card className="p-5 border-amber-300 bg-amber-50/40" data-testid="drafts-panel">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold inline-flex items-center gap-1.5">
                <FileEdit className="w-3.5 h-3.5" /> Bozze salvate
              </div>
              <h2 className="font-heading text-lg font-semibold text-slate-900">{drafts.length} bozza{drafts.length === 1 ? "" : "e"} da completare</h2>
              <p className="text-sm text-slate-600 mt-1">
                Riprendi una bozza per modificarla e inviarla. Le polizze in bozza non sono ancora versate.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {drafts.map((d) => (
              <div
                key={d.id}
                className={`flex items-center gap-3 p-3 rounded-lg bg-white border ${editingDraftId === d.id ? "border-blue-400 ring-2 ring-blue-200" : "border-amber-200"}`}
                data-testid={`draft-row-${d.id}`}
              >
                <div className="flex-1">
                  <div className="font-medium text-slate-900">
                    {d.n_polizze} polizz{d.n_polizze === 1 ? "a" : "e"} · {formatEUR(d.totale)}
                    {d.stale_count > 0 && (
                      <span className="ml-2 text-xs text-red-700 bg-red-50 px-2 py-0.5 rounded">
                        {d.stale_count} non più disponibili
                      </span>
                    )}
                    {editingDraftId === d.id && (
                      <span className="ml-2 text-xs text-blue-700 bg-blue-50 px-2 py-0.5 rounded">In modifica</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    {d.destinazione === "conto_corrente" ? "Versa su C/C agenzia" : "Versa in agenzia"}
                    {d.note && <span> · &laquo;{d.note}&raquo;</span>}
                  </div>
                </div>
                <Button
                  size="sm" variant="outline"
                  onClick={() => openDraft(d)}
                  disabled={editingDraftId === d.id}
                  data-testid={`draft-open-${d.id}`}
                >
                  <ChevronRight className="w-3.5 h-3.5 mr-1" /> {editingDraftId === d.id ? "Selezionata" : "Modifica"}
                </Button>
                <Button
                  size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50"
                  onClick={() => deleteDraft(d.id)}
                  data-testid={`draft-delete-${d.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!isImportoLiberoMode && editingDraftId && (
        <div className="rounded-lg border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-900 flex items-center gap-2" data-testid="editing-draft-banner">
          <FileEdit className="w-4 h-4 text-blue-700" />
          <span><strong>Stai modificando una bozza.</strong> Aggiungi/rimuovi polizze e poi salva di nuovo come bozza o conferma e invia.</span>
        </div>
      )}

      {!isImportoLiberoMode && data?.grouped?.length > 0 && (
        <Card className="p-5 border-slate-200 md:sticky md:top-2 z-20 card-elevated" data-testid="versamento-summary-card">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Selezionate</div>
                <div className="font-heading text-xl font-bold text-slate-900">
                  {countSelected} polizze · <span className="text-emerald-700">{formatEUR(totalSelected)}</span>
                </div>
              </div>
              <div className="flex-1 md:max-w-xs">
                <Textarea placeholder="Note (facoltative)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="versamento-note" />
              </div>
            </div>
            <div className="border-t border-slate-100 pt-4">
              {isAgente ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 inline-flex items-start gap-2" data-testid="versamento-destinazione-fixed">
                  <Banknote className="w-4 h-4 mt-0.5 text-slate-500" />
                  <div>
                    <div className="font-semibold text-slate-900">Destinazione: C/C agenzia</div>
                    <div className="text-xs text-slate-500">Il versamento verrà accreditato sul conto corrente dell&apos;agenzia.</div>
                  </div>
                </div>
              ) : data?.has_cc_separato && gestioneLiquiditaAttiva ? (
                <>
                  <div className="text-xs text-slate-500 uppercase font-semibold tracking-wider mb-2">Destinazione del versamento</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="versamento-destinazione">
                    <button
                      type="button"
                      onClick={() => setDestinazione("conto_corrente")}
                      className={`text-left p-3 rounded-lg border ${destinazione === "conto_corrente" ? "border-emerald-700 bg-emerald-700 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
                      data-testid="destinazione-cc-separato"
                    >
                      <div className="font-semibold text-sm">🏦 Versa su CC separato</div>
                      <div className={`text-xs mt-0.5 ${destinazione === "conto_corrente" ? "text-emerald-100" : "text-slate-500"}`}>Step intermedio: il liquido va sul tuo CC separato (auto-convalida). Solo polizze incassate in contante / assegno / recuperi.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDestinazione("agenzia")}
                      className={`text-left p-3 rounded-lg border ${destinazione === "agenzia" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
                      data-testid="destinazione-agenzia"
                    >
                      <div className="font-semibold text-sm">🏢 Versa in agenzia</div>
                      <div className={`text-xs mt-0.5 ${destinazione === "agenzia" ? "text-slate-300" : "text-slate-500"}`}>Step finale: il premio viene accreditato sul CC dell&apos;agenzia. L&apos;agente convalida quando vede l&apos;accredito.</div>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-xs text-slate-500 uppercase font-semibold tracking-wider mb-2">Destinazione del versamento</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="versamento-destinazione">
                    {!allowAgenzia && !allowCc && <NoDestNotice />}
                    {allowAgenzia && (
                    <button
                      type="button"
                      onClick={() => setDestinazione("agenzia")}
                      className={`text-left p-3 rounded-lg border ${destinazione === "agenzia" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
                      data-testid="destinazione-agenzia"
                    >
                      <div className="font-semibold text-sm">Versa in agenzia</div>
                      <div className={`text-xs mt-0.5 ${destinazione === "agenzia" ? "text-slate-300" : "text-slate-500"}`}>Il contante/assegno viene fisicamente portato in cassa di agenzia (aggiorna la cassa dell&apos;agente).</div>
                    </button>
                    )}
                    {allowCc && (
                    <button
                      type="button"
                      onClick={() => setDestinazione("conto_corrente")}
                      className={`text-left p-3 rounded-lg border ${destinazione === "conto_corrente" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
                      data-testid="destinazione-cc"
                    >
                      <div className="font-semibold text-sm">Versa su C/C agenzia</div>
                      <div className={`text-xs mt-0.5 ${destinazione === "conto_corrente" ? "text-slate-300" : "text-slate-500"}`}>Il denaro va sul conto corrente dell&apos;agenzia (non aggiorna la cassa fisica).</div>
                    </button>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-2">
              {editingDraftId && (
                <Button
                  onClick={cancelDraftEdit}
                  variant="outline"
                  size="lg"
                  data-testid="cancel-draft-edit"
                >
                  Annulla modifica bozza
                </Button>
              )}
              <Button
                onClick={saveDraft}
                disabled={submitting || countSelected === 0 || noDest}
                variant="outline"
                size="lg"
                data-testid="versamento-save-draft"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingDraftId ? "Aggiorna bozza" : "Salva come bozza"}
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || countSelected === 0 || noDest}
                size="lg"
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="versamento-submit"
              >
                <Send className="w-4 h-4 mr-2" />
                {submitting ? "Invio in corso…" : editingDraftId ? "Conferma e invia" : "Salva e invia"}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {!isImportoLiberoMode && (!data || data.grouped.length === 0) && (
        <Card className="p-12 text-center border-slate-200" data-testid="empty-versamento">
          <Banknote className="w-12 h-12 text-slate-300 mx-auto" />
          <h3 className="font-heading text-lg font-semibold text-slate-900 mt-4">Nessuna polizza da versare</h3>
          <p className="text-sm text-slate-500 mt-1">Tutti gli incassi disponibili sono già stati versati.</p>
        </Card>
      )}

      {!isImportoLiberoMode && data?.grouped?.map((g) => {
        const allSel = allDaySelected(g.entries);
        return (
          <Card key={g.date} className="border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-4 py-3 flex items-center justify-between border-b border-slate-200">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={allSel}
                  onCheckedChange={(v) => toggleDay(g.entries, !!v)}
                  data-testid={`day-toggle-${g.date}`}
                />
                <div>
                  <div className="font-heading font-semibold text-slate-900">Giornata {formatDateIT(g.date)}</div>
                  <div className="text-xs text-slate-500">{g.entries.length} polizze · totale {formatEUR(g.total)}</div>
                </div>
              </label>
              <span className="text-xs text-slate-500 hidden md:inline">Versa intera giornata</span>
            </div>
            <table className="w-full text-sm">
              <thead className="hidden sm:table-header-group">
                <tr className="text-left">
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 w-10"></th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Contraente</th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">N. Polizza</th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Metodo</th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Importo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {g.entries.map((e) => {
                  const inOtherDraft = !!e.in_draft_id && e.in_draft_id !== editingDraftId;
                  return (
                  <tr key={e.id}
                      className={selected[e.id] ? "bg-blue-50/50" : inOtherDraft ? "bg-amber-50/40" : "hover:bg-slate-50"}
                      data-testid={`entry-${e.id}`}>
                    <td className="px-4 py-3">
                      <Checkbox
                        checked={!!selected[e.id]}
                        disabled={inOtherDraft}
                        onCheckedChange={() => toggleEntry(e.id)}
                        data-testid={`entry-cb-${e.id}`}
                      />
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {e.contraente}
                      {e.urgente && <AlertTriangle className="w-3.5 h-3.5 text-red-600 inline-block ml-2" />}
                      {inOtherDraft && (
                        <span className="ml-2 text-[10px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">In bozza</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700 font-mono text-xs">{e.numero_polizza}</td>
                    <td className="px-4 py-3 text-slate-700">{e.metodo_pagamento}</td>
                    <td className="px-4 py-3 text-right font-mono-num font-semibold">{formatEUR(e.importo)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        );
      })}


      {/* Past versamenti */}
      <div>
        <h2 className="font-heading text-xl font-semibold text-slate-900 mt-8 mb-3">Versamenti effettuati</h2>
        {history.length === 0 ? (
          <p className="text-sm text-slate-500">Nessun versamento ancora registrato.</p>
        ) : (
          <Card className="border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Data</th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Polizze</th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Totale</th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Stato</th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Allegati</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.map((v) => {
                  const sc = v.stato_convalida || "convalidato";
                  const statoCfg = sc === "in_attesa"
                    ? { label: "In attesa di convalida", cls: "bg-amber-100 text-amber-800" }
                    : sc === "rifiutato"
                      ? { label: "Rifiutato", cls: "bg-red-100 text-red-800" }
                      : { label: "Convalidato", cls: "bg-emerald-100 text-emerald-800" };
                  return (
                    <tr key={v.id} className="hover:bg-slate-50" data-testid={`versamento-row-${v.id}`}>
                      <td className="px-4 py-3 font-medium">{formatDateIT(v.data_versamento)}</td>
                      <td className="px-4 py-3 text-right font-mono-num">{v.n_polizze}</td>
                      <td className="px-4 py-3 text-right font-mono-num font-semibold">{formatEUR(v.totale)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${statoCfg.cls}`}
                              data-testid={`versamento-stato-${v.id}`}>{statoCfg.label}</span>
                        {sc === "rifiutato" && v.convalida_note && (
                          <div className="text-xs text-red-700 mt-1 italic">Motivazione: {v.convalida_note}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => downloadVersamento(v.id, v.user?.cognome, v.data_versamento, "pdf")}><FileDown className="w-3.5 h-3.5 mr-1" /> PDF</Button>{" "}
                        <Button size="sm" variant="outline" onClick={() => downloadVersamento(v.id, v.user?.cognome, v.data_versamento, "excel")}><FileDown className="w-3.5 h-3.5 mr-1" /> Excel</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  );
}
